// Thin wrapper around the Court Movement League endpoints exposed by
// /app/backend/event_engine.py. Uses the same axios instance + Bearer
// token interceptor as the rest of the app via `api`. No new auth state.
import axios from 'axios';
import { tokenStore } from './api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const eventAPI = {
  // Profile picker for the registration UI
  listEligibleProfiles: () => client.get('/event-engine/profiles'),
  // Session-scoped picker — visible to admin or assigned Event Manager,
  // returns only safe fields (no email/phone).
  listAvailablePlayers: (sessionId) =>
    client.get(`/event-sessions/${sessionId}/available-players`),
  // Sessions
  listSessions: (params) => client.get('/event-sessions', { params }),
  listMySessions: () => client.get('/event-sessions/mine'),
  createSession: (payload) => client.post('/event-sessions', payload),
  getSession: (id) => client.get(`/event-sessions/${id}`),
  updateSession: (id, patch) => client.patch(`/event-sessions/${id}`, patch),
  deleteSession: (id) => client.delete(`/event-sessions/${id}`),
  archiveSession: (id) => client.post(`/event-sessions/${id}/archive`),
  restoreSession: (id) => client.post(`/event-sessions/${id}/restore`),
  // Registrations
  addRegistration: (sessionId, payload) =>
    client.post(`/event-sessions/${sessionId}/registrations`, payload),
  updateRegistration: (sessionId, regId, patch) =>
    client.patch(`/event-sessions/${sessionId}/registrations/${regId}`, patch),
  deleteRegistration: (sessionId, regId) =>
    client.delete(`/event-sessions/${sessionId}/registrations/${regId}`),
  // Start
  startSession: (id) => client.post(`/event-sessions/${id}/start`),
  startPreview: (id) => client.get(`/event-sessions/${id}/start-preview`),
  // Weeks
  listWeeks: (id) => client.get(`/event-sessions/${id}/weeks`),
  getWeek: (id, n) => client.get(`/event-sessions/${id}/weeks/${n}`),
  saveWeekPoints: (id, n, slots) =>
    client.post(`/event-sessions/${id}/weeks/${n}/points`, { slots }),
  finalizeWeek: (id, n, tieBreakers) =>
    client.post(`/event-sessions/${id}/weeks/${n}/finalize`, { tie_breakers: tieBreakers || null }),
  reopenWeek: (id, n, reason) =>
    client.post(`/event-sessions/${id}/weeks/${n}/reopen`, { reason }),
  // Lock Court Movement (per-week, per-court). Admin/EM only on the
  // backend; frontend hides the toggle for everyone else.
  setCourtMovementLock: (id, n, courtNumber, movementLocked) =>
    client.patch(`/event-sessions/${id}/weeks/${n}/courts/${courtNumber}/lock`, { movement_locked: !!movementLocked }),
  // Admin/EM-only full week assignment editor. Backend validates the
  // entire layout and refuses if any score/sub/no-show has been entered.
  editWeekAssignments: (id, n, courts) =>
    client.patch(`/event-sessions/${id}/weeks/${n}/assignments`, { courts }),
  // Standings
  getStandings: (id) => client.get(`/event-sessions/${id}/standings`),
  // Self-register (Phase 2.C) — optional shared_profile_id (Phase B)
  selfRegister: (id, role, sharedProfileId) =>
    client.post(`/event-sessions/${id}/self-register`,
      Object.assign({}, role ? { role } : {}, sharedProfileId ? { shared_profile_id: sharedProfileId } : {})),
  // Find Sub (Phase A) — Sub Pool contact list + sub-coverage requests
  getSubPool: (id) => client.get(`/event-sessions/${id}/sub-pool`),
  listSubRequests: (id) => client.get(`/event-sessions/${id}/sub-requests`),
  createSubRequest: (id, payload) =>
    client.post(`/event-sessions/${id}/sub-requests`, payload),
  createOtherSubRequest: (id, payload) =>
    client.post(`/event-sessions/${id}/sub-requests/other`, payload),
  resolveSubRequest: (id, reqId, patch) =>
    client.patch(`/event-sessions/${id}/sub-requests/${reqId}`, patch),
  // Shared Session (Phase B)
  listSharedPartners: (id) => client.get(`/event-sessions/${id}/shared-partners`),
  listEligibleSharedPartners: (id) => client.get(`/event-sessions/${id}/eligible-shared-partners`),
  setSharedPartner: (id, regId, payload) =>
    client.post(`/event-sessions/${id}/registrations/${regId}/shared-partner`, payload),
  removeSharedPartner: (id, regId) =>
    client.delete(`/event-sessions/${id}/registrations/${regId}/shared-partner`),
};
